import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Table, Spinner, Badge } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { getBackendUrl } from '../config';
import './Settings.scss';

/**
 * AdminSkills ermöglicht es Administratoren, globale Unterrichts-Fächer
 * (Skills) im System anzulegen, zu aktualisieren und zu löschen.
 */
export default function AdminSkills() {
  const { token } = useAuth();
  
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [newSkillName, setNewSkillName] = useState('');
  
  const [editSkillId, setEditSkillId] = useState(null);
  const [editSkillName, setEditSkillName] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [archiveCandidate, setArchiveCandidate] = useState(null);
  const [filterType, setFilterType] = useState('active'); // 'active' | 'archived' | 'all'

  /**
   * Hilfsfunktion zur Ermittlung detaillierter API-Fehlermeldungen.
   */
  const handleResponseError = async (response, defaultMessage) => {
    let errorMsg = defaultMessage;
    try {
      const errData = await response.json();
      errorMsg = errData.message || errData.error || errorMsg;
    } catch (e) {
      if (response.statusText) {
        errorMsg = `${defaultMessage} (${response.status} - ${response.statusText})`;
      } else {
        errorMsg = `${defaultMessage} (Status-Code: ${response.status})`;
      }
    }
    return new Error(errorMsg);
  };

  /**
   * Lädt alle verfügbaren Skills aus dem Backend.
   */
  const fetchSkills = async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${getBackendUrl()}/skill`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        throw await handleResponseError(response, 'Skills konnten nicht geladen werden.');
      }
      const data = await response.json();
      setSkills(data.sort((a, b) => a.id - b.id));
    } catch (err) {
      setError(err.message || 'Fehler beim Laden der Skills.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSkills();
  }, [token]);

  /**
   * Erstellt einen neuen globalen Skill im Backend.
   */
  const handleAddSkill = async (e) => {
    e.preventDefault();
    if (!newSkillName.trim()) return;
    
    setError('');
    setSuccess('');
    setSubmitting(true);
    
    try {
      const response = await fetch(`${getBackendUrl()}/skill`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: newSkillName.trim() })
      });
      
      if (!response.ok) {
        throw await handleResponseError(response, 'Fehler beim Erstellen des Skills.');
      }
      
      setSuccess(`Skill "${newSkillName.trim()}" erfolgreich hinzugefügt.`);
      setNewSkillName('');
      await fetchSkills();
    } catch (err) {
      setError(err.message || 'Fehler beim Hinzufügen des Skills.');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Aktualisiert den Namen eines bestehenden Skills im Backend.
   */
  const handleUpdateSkill = async (id) => {
    if (!editSkillName.trim()) return;
    
    setError('');
    setSuccess('');
    setSubmitting(true);
    
    try {
      const response = await fetch(`${getBackendUrl()}/skill`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id, name: editSkillName.trim() })
      });
      
      if (!response.ok) {
        throw await handleResponseError(response, 'Fehler beim Aktualisieren des Skills.');
      }
      
      setSuccess(`Skill erfolgreich aktualisiert.`);
      setEditSkillId(null);
      setEditSkillName('');
      await fetchSkills();
    } catch (err) {
      setError(err.message || 'Fehler beim Aktualisieren.');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Löscht einen Skill aus dem Backend.
   */
  const handleDeleteSkill = async (id) => {
    setError('');
    setSuccess('');
    setArchiveCandidate(null);
    setSubmitting(true);
    
    try {
      const response = await fetch(`${getBackendUrl()}/skill/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const skillToArchive = skills.find(s => s.id === id);
        if (skillToArchive) {
          setArchiveCandidate(skillToArchive);
        }
        throw await handleResponseError(response, 'Fehler beim Löschen des Skills.');
      }
      
      setSuccess(`Skill erfolgreich gelöscht.`);
      setDeleteConfirmId(null);
      await fetchSkills();
    } catch (err) {
      setError(err.message || 'Fehler beim Löschen.');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Archiviert einen Skill durch Umbenennung in '[Name]-deleted'.
   */
  const handleArchiveSkill = async (id, currentName) => {
    setError('');
    setSuccess('');
    setSubmitting(true);
    
    try {
      const archivedName = `${currentName.trim()}-deleted`;
      const response = await fetch(`${getBackendUrl()}/skill`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id, name: archivedName })
      });
      
      if (!response.ok) {
        throw await handleResponseError(response, 'Fehler beim Archivieren des Skills.');
      }
      
      setSuccess(`Skill wurde erfolgreich archiviert als "${archivedName}".`);
      setArchiveCandidate(null);
      setDeleteConfirmId(null);
      await fetchSkills();
    } catch (err) {
      setError(err.message || 'Fehler beim Archivieren.');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Reaktiviert einen archivierten Skill durch Entfernen von '-deleted'.
   */
  const handleReactivateSkill = async (id, currentName) => {
    setError('');
    setSuccess('');
    setSubmitting(true);
    
    try {
      let reactivatedName = currentName.trim();
      if (reactivatedName.toLowerCase().endsWith('-deleted')) {
        reactivatedName = reactivatedName.slice(0, -8);
      }
      
      const response = await fetch(`${getBackendUrl()}/skill`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id, name: reactivatedName })
      });
      
      if (!response.ok) {
        throw await handleResponseError(response, 'Fehler beim Reaktivieren des Skills.');
      }
      
      setSuccess(`Skill "${reactivatedName}" wurde erfolgreich reaktiviert.`);
      await fetchSkills();
    } catch (err) {
      setError(err.message || 'Fehler beim Reaktivieren.');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (skill) => {
    setEditSkillId(skill.id);
    setEditSkillName(skill.name);
    setDeleteConfirmId(null);
  };

  const cancelEdit = () => {
    setEditSkillId(null);
    setEditSkillName('');
  };

  const filteredSkills = skills.filter(skill => {
    const matchesSearch = skill.name.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    
    const isDeleted = skill.name.toLowerCase().endsWith('-deleted');
    if (filterType === 'active') {
      return !isDeleted;
    } else if (filterType === 'archived') {
      return isDeleted;
    }
    return true; // 'all'
  });

  return (
    <div className="vintage-settings p-2 p-sm-3 p-md-4" style={{ backgroundColor: '#fcfaf8', minHeight: '100vh', borderRadius: '12px' }}>
      <h1 className="page-header" style={{ color: '#2c3e50', fontWeight: '700' }}>Skills verwalten</h1>
      
      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      {archiveCandidate && (
        <Alert variant="warning" className="shadow-sm mb-4 border-0" style={{ borderRadius: '12px', backgroundColor: '#fffbeb', color: '#b45309' }}>
          <h6 className="alert-heading fw-bold mb-2">
            <i className="bi bi-exclamation-triangle-fill me-2"></i> Skill wird verwendet
          </h6>
          <p className="small mb-3" style={{ color: '#d97706', lineHeight: '1.5' }}>
            Der Skill <strong>"{archiveCandidate.name}"</strong> ist bereits mit Dozenten-Profilen, Anfragen oder Lerneinheiten verknüpft und kann nicht physisch gelöscht werden.
            Möchten Sie diesen Skill stattdessen archivieren? Er wird aus allen zukünftigen Suchen und Profilauswahlen ausgeblendet, bleibt aber in der Buchungshistorie sichtbar.
          </p>
          <div className="d-flex gap-2">
            <Button 
              variant="warning" 
              size="sm" 
              onClick={() => handleArchiveSkill(archiveCandidate.id, archiveCandidate.name)}
              disabled={submitting}
              style={{ fontWeight: '600' }}
            >
              Ja, archivieren
            </Button>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={() => setArchiveCandidate(null)}
              disabled={submitting}
            >
              Abbrechen
            </Button>
          </div>
        </Alert>
      )}

      <Card className="settings-card mb-4 shadow-sm border-0">
        <Card.Header className="bg-white border-bottom">
          <span style={{ fontWeight: '600' }}>Neuen Skill anlegen</span>
        </Card.Header>
        <Form onSubmit={handleAddSkill}>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label className="form-label">Skill-Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="z. B. Advanced React, Chemie, Python..."
                value={newSkillName}
                onChange={(e) => setNewSkillName(e.target.value)}
                required
                disabled={submitting}
                className="form-control"
              />
            </Form.Group>
          </Card.Body>
          <Card.Footer className="bg-white border-top">
            <Button 
              type="submit" 
              className="form-button" 
              disabled={submitting || !newSkillName.trim()} 
              style={{ backgroundColor: '#b95c50', borderColor: '#b95c50' }}
            >
              {submitting ? <Spinner animation="border" size="sm" className="me-2" /> : 'Skill hinzufügen'}
            </Button>
          </Card.Footer>
        </Form>
      </Card>

      <Card className="settings-card shadow-sm border-0">
        <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center flex-wrap gap-3">
          <span style={{ fontWeight: '600' }}>Vorhandene Skills ({filteredSkills.length})</span>
          
          <div className="d-flex align-items-center gap-3 flex-wrap">
            {/* Pill-Filter */}
            <div className="d-flex bg-light p-1 rounded-pill" style={{ border: '1px solid #eae5e0' }}>
              <Button
                variant={filterType === 'active' ? 'primary' : 'link'}
                size="sm"
                className="rounded-pill px-3 py-1"
                style={{
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  color: filterType === 'active' ? '#fff' : '#665e56',
                  backgroundColor: filterType === 'active' ? '#b95c50' : 'transparent',
                  borderColor: filterType === 'active' ? '#b95c50' : 'transparent',
                  textDecoration: 'none'
                }}
                onClick={() => setFilterType('active')}
              >
                Aktiv ({skills.filter(s => !s.name.toLowerCase().endsWith('-deleted')).length})
              </Button>
              <Button
                variant={filterType === 'archived' ? 'primary' : 'link'}
                size="sm"
                className="rounded-pill px-3 py-1"
                style={{
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  color: filterType === 'archived' ? '#fff' : '#665e56',
                  backgroundColor: filterType === 'archived' ? '#b95c50' : 'transparent',
                  borderColor: filterType === 'archived' ? '#b95c50' : 'transparent',
                  textDecoration: 'none'
                }}
                onClick={() => setFilterType('archived')}
              >
                Archiviert ({skills.filter(s => s.name.toLowerCase().endsWith('-deleted')).length})
              </Button>
              <Button
                variant={filterType === 'all' ? 'primary' : 'link'}
                size="sm"
                className="rounded-pill px-3 py-1"
                style={{
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  color: filterType === 'all' ? '#fff' : '#665e56',
                  backgroundColor: filterType === 'all' ? '#b95c50' : 'transparent',
                  borderColor: filterType === 'all' ? '#b95c50' : 'transparent',
                  textDecoration: 'none'
                }}
                onClick={() => setFilterType('all')}
              >
                Alle ({skills.length})
              </Button>
            </div>
            
            {/* Search Input */}
            <div style={{ maxWidth: '200px', width: '100%' }}>
              <Form.Control
                type="text"
                placeholder="Suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ borderRadius: '8px' }}
                className="form-control py-1 px-2"
              />
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="md" className="mb-2" style={{ color: '#b95c50' }} />
              <div className="text-muted">Skills werden geladen...</div>
            </div>
          ) : filteredSkills.length === 0 ? (
            <div className="text-center py-5 text-muted">
              {searchTerm ? 'Keine passenden Skills gefunden.' : 'Noch keine Skills eingepflegt.'}
            </div>
          ) : (
            <>
              {/* Desktop-Ansicht: Tabelle */}
              <div className="table-responsive d-none d-md-block">
                <Table hover className="align-middle mb-0 text-nowrap" style={{ backgroundColor: 'transparent' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#fdfaf7', borderBottom: '1px solid #eae5e0' }}>
                      <th style={{ padding: '1rem 1.5rem', width: '80px', color: '#7f8c8d', fontSize: '0.85rem' }}>ID</th>
                      <th style={{ padding: '1rem 1.5rem', color: '#7f8c8d', fontSize: '0.85rem' }}>Name</th>
                      <th style={{ padding: '1rem 1.5rem', width: '240px', color: '#7f8c8d', fontSize: '0.85rem', textAlign: 'right' }}>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSkills.map(skill => (
                      <tr key={skill.id} style={{ borderBottom: '1px solid #eae5e0' }}>
                        <td style={{ padding: '1rem 1.5rem', fontWeight: '500' }}>#{skill.id}</td>
                        <td style={{ padding: '1rem 1.5rem' }}>
                          {editSkillId === skill.id ? (
                            <Form.Control
                              type="text"
                              value={editSkillName}
                              onChange={(e) => setEditSkillName(e.target.value)}
                              size="sm"
                              required
                              style={{ maxWidth: '300px', borderRadius: '8px' }}
                              className="form-control"
                            />
                          ) : (
                            <>
                              <span style={{ 
                                fontSize: '1.05rem', 
                                color: skill.name.toLowerCase().endsWith('-deleted') ? '#7f8c8d' : '#2c3e50', 
                                fontWeight: '500',
                                textDecoration: skill.name.toLowerCase().endsWith('-deleted') ? 'line-through' : 'none'
                              }}>
                                {skill.name.toLowerCase().endsWith('-deleted') ? skill.name.slice(0, -8) : skill.name}
                              </span>
                              {skill.name.toLowerCase().endsWith('-deleted') && (
                                <Badge bg="secondary" className="ms-2" style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '0.75rem', fontWeight: '600' }}>Archiviert</Badge>
                              )}
                            </>
                          )}
                        </td>
                        <td style={{ padding: '1rem 1.5rem', textAlign: 'right' }}>
                          {editSkillId === skill.id ? (
                            <div className="d-flex justify-content-end gap-2">
                              <Button 
                                variant="success" 
                                size="sm" 
                                onClick={() => handleUpdateSkill(skill.id)} 
                                disabled={submitting || !editSkillName.trim()}
                                style={{ borderRadius: '8px', padding: '6px 12px' }}
                              >
                                Speichern
                              </Button>
                              <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                onClick={cancelEdit} 
                                disabled={submitting}
                                style={{ borderRadius: '8px', padding: '6px 12px' }}
                              >
                                Abbrechen
                              </Button>
                            </div>
                          ) : deleteConfirmId === skill.id ? (
                            <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
                              <span className="text-danger" style={{ fontSize: '0.85rem', fontWeight: '600' }}>Sicher?</span>
                              <Button 
                                variant="danger" 
                                size="sm" 
                                onClick={() => handleDeleteSkill(skill.id)} 
                                disabled={submitting}
                                style={{ borderRadius: '8px', padding: '4px 10px' }}
                              >
                                Ja, löschen
                              </Button>
                              <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                onClick={() => setDeleteConfirmId(null)} 
                                disabled={submitting}
                                style={{ borderRadius: '8px', padding: '4px 10px' }}
                              >
                                Nein
                              </Button>
                            </div>
                          ) : (
                            <div className="d-flex justify-content-end gap-2">
                              <Button 
                                variant="outline-primary" 
                                size="sm" 
                                onClick={() => startEdit(skill)}
                                disabled={submitting || editSkillId !== null}
                                style={{ borderRadius: '8px', padding: '6px 12px', borderColor: '#b95c50', color: '#b95c50' }}
                                className="vintage-edit-btn"
                              >
                                Bearbeiten
                              </Button>
                              {skill.name.toLowerCase().endsWith('-deleted') ? (
                                <Button 
                                  variant="outline-success" 
                                  size="sm" 
                                  onClick={() => handleReactivateSkill(skill.id, skill.name)}
                                  disabled={submitting || editSkillId !== null}
                                  style={{ borderRadius: '8px', padding: '6px 12px' }}
                                >
                                  Reaktivieren
                                </Button>
                              ) : (
                                <Button 
                                  variant="outline-danger" 
                                  size="sm" 
                                  onClick={() => { setDeleteConfirmId(skill.id); cancelEdit(); }}
                                  disabled={submitting || editSkillId !== null}
                                  style={{ borderRadius: '8px', padding: '6px 12px' }}
                                >
                                  Löschen
                                </Button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Mobile-Ansicht: Kachel-Kartenliste */}
              <div className="d-md-none p-3" style={{ backgroundColor: '#fdfbf9' }}>
                {filteredSkills.map(skill => (
                  <div key={skill.id} className="p-3 mb-3 border rounded bg-white shadow-sm d-flex flex-column gap-2" style={{ borderColor: '#eae5e0', borderRadius: '12px' }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small" style={{ fontWeight: '600' }}>ID: #{skill.id}</span>
                    </div>
                    <div className="fw-bold fs-6 text-dark" style={{ fontFamily: "'Lora', serif" }}>
                      {editSkillId === skill.id ? (
                        <Form.Control
                          type="text"
                          value={editSkillName}
                          onChange={(e) => setEditSkillName(e.target.value)}
                          size="sm"
                          required
                          className="form-control"
                          style={{ borderRadius: '8px' }}
                        />
                      ) : skill.name.toLowerCase().endsWith('-deleted') ? (
                        <>
                          <span style={{ textDecoration: 'line-through', color: '#7f8c8d' }}>{skill.name.slice(0, -8)}</span>
                          <Badge bg="secondary" className="ms-2" style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '0.7rem', fontWeight: '600' }}>Archiviert</Badge>
                        </>
                      ) : (
                        skill.name
                      )}
                    </div>
                    <div className="d-flex justify-content-end mt-2 pt-2 border-top" style={{ borderColor: '#eae5e0' }}>
                      {editSkillId === skill.id ? (
                        <div className="d-flex gap-2 w-100">
                          <Button variant="success" size="sm" className="w-50" style={{ borderRadius: '8px' }} onClick={() => handleUpdateSkill(skill.id)} disabled={submitting || !editSkillName.trim()}>Speichern</Button>
                          <Button variant="outline-secondary" size="sm" className="w-50" style={{ borderRadius: '8px' }} onClick={cancelEdit} disabled={submitting}>Abbrechen</Button>
                        </div>
                      ) : deleteConfirmId === skill.id ? (
                        <div className="d-flex gap-2 align-items-center justify-content-between w-100">
                          <span className="text-danger small fw-bold">Diesen Skill wirklich löschen?</span>
                          <div className="d-flex gap-2">
                            <Button variant="danger" size="sm" style={{ borderRadius: '8px', padding: '4px 12px' }} onClick={() => handleDeleteSkill(skill.id)} disabled={submitting}>Ja</Button>
                            <Button variant="outline-secondary" size="sm" style={{ borderRadius: '8px', padding: '4px 12px' }} onClick={() => setDeleteConfirmId(null)} disabled={submitting}>Nein</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="d-flex gap-2 w-100">
                          <Button variant="outline-primary" size="sm" className="w-50" style={{ borderRadius: '8px', borderColor: '#b95c50', color: '#b95c50' }} onClick={() => startEdit(skill)} disabled={submitting || editSkillId !== null}>Bearbeiten</Button>
                          {skill.name.toLowerCase().endsWith('-deleted') ? (
                            <Button variant="outline-success" size="sm" className="w-50" style={{ borderRadius: '8px' }} onClick={() => handleReactivateSkill(skill.id, skill.name)} disabled={submitting || editSkillId !== null}>Reaktivieren</Button>
                          ) : (
                            <Button variant="outline-danger" size="sm" className="w-50" style={{ borderRadius: '8px' }} onClick={() => { setDeleteConfirmId(skill.id); cancelEdit(); }} disabled={submitting || editSkillId !== null}>Löschen</Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
